import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { filterBarDataPointsWithUndefinedValue, filterDataPointsWithUndefinedValue, LineData, tokens } from 'ComponentLibrary'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

export const getDevelopment = (valuations: ExistingValuation[], tCommon: TFunction<'terminology', 'common'>): LineData[] => {
    valuations.sort((a, b) => dayjs(b.valuation_date).diff(dayjs(a.valuation_date)))
    const predictions = getDevelopmentPredictions(valuations[0])
    const dataPoints = valuations
        .filter((valuation) => valuation.valuation_date !== undefined)
        .filter((valuation) => !valuation.is_draft)
        .map((valuation) => ({
            date: valuation.valuation_date as string,
            value: valuation.market_value_with_trend.value?.amount,
        }))

    return [
        {
            id: tCommon('valueTrend'),
            data: filterDataPointsWithUndefinedValue([...predictions, ...dataPoints]),
            color: tokens.ColorGraphBinaryPositiveMedium,
        },
    ]
}

export const getIncome = (
    valuations: ExistingValuation[],
    tAssetKeyValues: TFunction<'pages', 'assets.assetDetails.assetKeyValues'>,
    tCommon: TFunction<'terminology', 'common'>,
    withActualRent = false
): LineData[] => {
    valuations.sort((a, b) => dayjs(b.valuation_date).diff(dayjs(a.valuation_date)))

    const rentPredictions = getRentPredictions(valuations[0])
    const rentDataPoints = valuations
        .filter((valuation) => valuation.valuation_date)
        .filter((valuation) => !valuation.is_draft)
        .map((valuation) => ({
            date: valuation.valuation_date as string,
            value: valuation?.tenant_summary?.total?.gross_rental_income.total.amount,
        }))

    const actualRentPredictions = getActualRentPredictions(valuations[0])
    const actualRentDataPoints = valuations
        .filter((valuation) => valuation.valuation_date)
        .filter((valuation) => !valuation.is_draft)
        .map((valuation) => ({
            date: valuation.valuation_date as string,
            value: valuation?.tenant_summary?.total?.actual_rental_income.total.amount,
        }))

    const costPredictions = getCostPredictions(valuations[0], tAssetKeyValues, tCommon)
    const costDataPoints = valuations
        .filter((valuation) => valuation.valuation_date)
        .filter((valuation) => !valuation.is_draft)
        .map(({ tenant_summary, valuation_date }) => ({
            date: valuation_date as string,
            value: tenant_summary?.total?.management_cost.total.amount,
            color: tokens.ColorGraphSequentialOneMedium,
            bars: filterBarDataPointsWithUndefinedValue([
                {
                    id: tAssetKeyValues('chartSeries.maintenanceCosts'),
                    value: tenant_summary?.total?.maintenance_cost.total.amount,
                    color: tokens.ColorGraphSequentialEightLight,
                },
                {
                    id: tAssetKeyValues('chartSeries.administrativeCosts'),
                    value: tenant_summary?.total?.administrative_cost.total.amount,
                    color: tokens.ColorGraphSequentialEightMedium,
                },
                {
                    id: tAssetKeyValues('chartSeries.lossOfRentRisk'),
                    value: tenant_summary?.total?.loss_of_rent_risk_cost.total.amount,
                    color: tokens.ColorGraphSequentialEightDark,
                },
                {
                    id: tCommon('other'),
                    value: tenant_summary?.total?.operating_cost.total.amount,
                    color: tokens.ColorGraphSequentialEightDarkest,
                },
            ]),
        }))

    return [
        withActualRent && {
            id: tCommon('contractRent'),
            showTrends: false,
            data: filterDataPointsWithUndefinedValue([...actualRentPredictions, ...actualRentDataPoints]),
            color: tokens.ColorGraphSequentialTwoLight,
        },
        {
            id: tCommon('marketRent'),
            data: filterDataPointsWithUndefinedValue([...rentPredictions, ...rentDataPoints]),
            color: tokens.ColorGraphSequentialTwoMedium,
        },
        {
            id: tAssetKeyValues('chartSeries.managementCosts'),
            data: filterDataPointsWithUndefinedValue([...costPredictions, ...costDataPoints]),
            color: tokens.ColorGraphSequentialOneMedium,
        },
    ].filter((item) => item !== false)
}

const getDevelopmentPredictions = (valuation: ExistingValuation) => {
    if (valuation.is_draft && valuation.valuation_date) {
        return [
            {
                date: valuation.valuation_date,
                value: valuation.market_value_with_trend.value.amount,
                isPrediction: true,
            },
        ]
    }
    return []
}

const getRentPredictions = (valuation: ExistingValuation) => {
    if (valuation.is_draft && valuation.valuation_date) {
        return [
            {
                date: valuation.valuation_date,
                value: valuation.tenant_summary?.total?.gross_rental_income.total.amount,
                isPrediction: true,
            },
        ]
    }
    return []
}

const getActualRentPredictions = (valuation: ExistingValuation) => {
    if (valuation.is_draft && valuation.valuation_date) {
        return [
            {
                date: valuation.valuation_date,
                value: valuation.tenant_summary?.total?.actual_rental_income.total.amount,
                isPrediction: true,
            },
        ]
    }
    return []
}

const getCostPredictions = (
    valuation: ExistingValuation,
    tAssetKeyValues: TFunction<'pages', 'assets.assetDetails.assetKeyValues'>,
    tCommon: TFunction<'terminology', 'common'>
) => {
    const rentTotal = valuation.tenant_summary?.total
    if (valuation.is_draft && valuation.valuation_date) {
        return [
            {
                date: valuation.valuation_date,
                value: rentTotal?.management_cost.total.amount,
                isPrediction: true,
                bars: filterBarDataPointsWithUndefinedValue([
                    {
                        id: tAssetKeyValues('chartSeries.maintenanceCosts'),
                        value: rentTotal?.maintenance_cost.total.amount,
                        color: tokens.ColorGraphSequentialEightLight,
                    },
                    {
                        id: tAssetKeyValues('chartSeries.administrativeCosts'),
                        value: rentTotal?.administrative_cost.total.amount,
                        color: tokens.ColorGraphSequentialEightMedium,
                    },
                    {
                        id: tAssetKeyValues('chartSeries.lossOfRentRisk'),
                        value: rentTotal?.loss_of_rent_risk_cost.total.amount,
                        color: tokens.ColorGraphSequentialEightDark,
                    },
                    {
                        id: tCommon('other'),
                        value: rentTotal?.operating_cost.total.amount,
                        color: tokens.ColorGraphSequentialEightDarkest,
                    },
                ]),
            },
        ]
    }
    return []
}
